import { useUtils } from "@/app/hooks/useUtils";
import BillingMirrorHeader from "../BillingMirrorHeader";
import { useEffect, useRef, useState, useMemo } from "react";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import NoDataFoundComponent from "@/app/components/ui/NoRecordFound";
import { BsExclamationCircle, BsCurrencyRupee } from "react-icons/bs";
import { ChevronDown, Dog, Cat, BadgeCheck, AlertTriangle } from "lucide-react";
import { useBookingSlotsListAPI } from "@/app/hooks/api_hooks/bookings/useBookingSlotsListAPI";
import { useSingleBookingDetailsAPI } from "@/app/hooks/api_hooks/bookings/useSingleBookingDetailsAPI";

interface MirrorFollowUpComponentProps {
  customerId?: string | null;
  vmsUserId?: string | null;
  recommendations?: any[] | null;
  slotsFilter?: any | null;
  selectedSlotId?: string | null;
  confirmedBookingId?: string | null;
  activePetId?: string | null;
  clinicName?: string | null;
  scrollCommand?: { direction: "up" | "down"; delta: number; ts: number } | null;
}

export const MirrorFollowUpComponent = ({
  customerId: _customerId,
  vmsUserId: _vmsUserId,
  recommendations,
  slotsFilter,
  selectedSlotId: selectedSlotIdProp,
  confirmedBookingId,
  activePetId,
  clinicName,
  scrollCommand,
}: MirrorFollowUpComponentProps) => {
  const { addAMPMToTime, veticDateFormat } = useUtils();
  const { BASIC } = useImageConstants();
  const fetchSlotsApi = useBookingSlotsListAPI();
  const fetchSingleBookingDetails = useSingleBookingDetailsAPI();
  const slotsScrollRef = useRef<HTMLDivElement | null>(null);
  const lastScrollTsRef = useRef<number | null>(null);
  const [availableSlots, setAvailableSlots] = useState<any[]>([]);
  const [isLoadingSlots, setIsLoadingSlots] = useState<boolean>(false);
  const [confirmedBookingDetails, setConfirmedBookingDetails] = useState<any | null>(null);
  const [isLoadingBookingDetails, setIsLoadingBookingDetails] = useState<boolean>(false);
  const activeRecommendations = recommendations ?? [];
  const [activeTab, setActiveTab] = useState(activeRecommendations[0]?.pet_id || "");
  const [selectedSlotId, setSelectedSlotId] = useState<string | null>(null);
  const [isBooked, setIsBooked] = useState(false);

  useEffect(() => {
    if (!scrollCommand) return;
    if (lastScrollTsRef.current === scrollCommand.ts) return;
    lastScrollTsRef.current = scrollCommand.ts;
    slotsScrollRef.current?.scrollBy({ top: scrollCommand.delta, behavior: "smooth" });
  }, [scrollCommand]);

  useEffect(() => {
    if (activeRecommendations[0]?.pet_id) {
      setActiveTab(activeRecommendations[0].pet_id);
    }
  }, [activeRecommendations]);

  // Sync active tab to match the slot filter pet_id
  useEffect(() => {
    if (slotsFilter?.pet_id) {
      setActiveTab(slotsFilter.pet_id);
    }
  }, [slotsFilter]);

  // Sync active tab to match the socket pet change
  useEffect(() => {
    if (activePetId) {
      setActiveTab(activePetId);
    }
  }, [activePetId]);

  // Sync slot selection from WebSocket
  useEffect(() => {
    if (selectedSlotIdProp !== undefined) {
      setSelectedSlotId(selectedSlotIdProp);
    }
  }, [selectedSlotIdProp]);

  // Clear slots local selection on tab/filter change
  useEffect(() => {
    setSelectedSlotId(null);
  }, [activeTab, slotsFilter]);

  // Fetch Booking Details when a booking is confirmed
  useEffect(() => {
    if (!confirmedBookingId) {
      setConfirmedBookingDetails(null);
      setIsLoadingBookingDetails(false);
      setIsBooked(false);
      return;
    }
    setIsLoadingBookingDetails(true);
    setIsBooked(true);
    fetchSingleBookingDetails(confirmedBookingId)
      .then((res: any) => {
        if (res) {
          const speciesName = res.pet_details?.pet_species_type_id === "2" ? "Cat" : "Dog";
          const isPrepaid =
            String(res.slot_type).toLowerCase() === "paid" ||
            res.prepaid_payment_status === "BOOKING_FEE" ||
            Number(res.booking_fee_amount) > 0;
          const details = {
            booking_id: res.booking_id,
            parent_name: res.user_details?.name || res.customer_details?.name || res.user?.name || res.customer_name,
            pet_name: res.pet_details?.name || "",
            pet_species_name: speciesName,
            pet_breed_name: res.pet_details?.pet_breed || "",
            date: res.booking_start_date,
            start_time: res.booking_start_time,
            end_time: res.booking_end_time,
            service_type_name: res.service_details?.name || "",
            sub_service_name: res.subservice_details?.name || "",
            staff_name: res.staff_details?.name || "",
            clinic_name: res.clinic_details?.display_name || res.clinic_details?.name || "",
            payment_collected: isPrepaid && res.payment_details?.payment_status === true,
            payment_pending: isPrepaid && res.payment_details?.payment_status !== true,
            booking_fee: res.payment_details?.amount || res.booking_fee_amount || 0,
            payment_number: res.customer_mobile,
          };
          setConfirmedBookingDetails(details);
        } else {
          setConfirmedBookingDetails(null);
        }
      })
      .catch((error) => {
        console.error("[FollowUp] Failed to fetch booking details:", error);
        setConfirmedBookingDetails(null);
      })
      .finally(() => {
        setIsLoadingBookingDetails(false);
      });
  }, [confirmedBookingId, fetchSingleBookingDetails]);

  const activeRec = activeRecommendations.find((rec) => rec.pet_id === activeTab) || activeRecommendations[0];

  // Fetch Slots from API when filter changes
  useEffect(() => {
    if (!slotsFilter) {
      setAvailableSlots([]);
      setIsLoadingSlots(false);
      return;
    }
    setIsLoadingSlots(true);
    const loadSlots = async () => {
      try {
        if (typeof fetchSlotsApi !== "function") {
          console.error("fetchSlotsApi is not callable", fetchSlotsApi);
          setAvailableSlots([]);
          return;
        }
        const res = await fetchSlotsApi(slotsFilter);
        if (res && res.status && res.message) {
          const responseData = res.message as any[];
          const groupedDoctors: any[] = [];
          responseData.forEach((clinic) => {
            (clinic.data ?? []).forEach((staff: any) => {
              if (slotsFilter.staff_ids?.length > 0 && !slotsFilter.staff_ids.includes(staff.staff_id)) {
                return;
              }
              const doctorSlots: any[] = [];
              (staff.data ?? []).forEach((slot: any) => {
                doctorSlots.push({
                  slot_id: slot.slot_id,
                  start_time: slot.start_time,
                  end_time: slot.end_time,
                  configuration_id: slot.configuration_id,
                  prepaid_booking_slot: slot.prepaid_booking_slot,
                  booking_fee: slot.booking_fee,
                  staff_id: staff.staff_id,
                  staff_name: staff.staff_name,
                });
              });
              if (doctorSlots.length > 0) {
                groupedDoctors.push({
                  staff_id: staff.staff_id,
                  staff_name: staff.staff_name,
                  slots: doctorSlots,
                });
              }
            });
          });
          setAvailableSlots(groupedDoctors);
        } else {
          setAvailableSlots([]);
        }
      } catch (error) {
        console.error("Fetch slots failed:", error);
        setAvailableSlots([]);
      } finally {
        setIsLoadingSlots(false);
      }
    };
    void loadSlots();
  }, [slotsFilter, fetchSlotsApi]);

  const displaySlots = availableSlots;
  const displayLoading = isLoadingSlots;
  const displayDate = slotsFilter?.booking_date || activeRec?.suggested_booking_date || "";

  // Dynamic prepaid banner data
  const { isPaidbooking, isPaidPriceAmount } = useMemo(() => {
    const prepaidSlots = displaySlots
      .flatMap((d) => d.slots)
      .filter((s) => s.prepaid_booking_slot?.toLowerCase() === "yes");
    return {
      isPaidbooking: prepaidSlots.length > 0,
      isPaidPriceAmount: prepaidSlots[0]?.booking_fee ?? null,
    };
  }, [displaySlots]);

  const prepaidBanner = isPaidbooking ? (
    <div className="bg-[#FFF2F2] p-[16px] w-full flex gap-3 rounded-[12px] my-2 border border-[#FFD3D3]">
      <div className="mt-1">
        <BsExclamationCircle className="w-6 h-6 text-red-500 text-base" />
      </div>
      <div>
        <p className="text-[20px] text-[#D01111] font-semibold flex items-center gap-2">Advance Payment Required</p>
        <p className="text-[16px] text-[#D01111] flex items-center mt-1">
          An advance payment of&nbsp;
          <span className="flex text-[16px] items-center">
            <BsCurrencyRupee className="w-4 h-4" />
          </span>
          &nbsp;
          <span className="font-bold"> {isPaidPriceAmount} </span>&nbsp; will be required for the prepaid slots
        </p>
      </div>
    </div>
  ) : null;

  const displaySuccessBooking = confirmedBookingDetails;
  const isPrepaidBooking = displaySuccessBooking?.payment_collected || displaySuccessBooking?.payment_pending;
  const isPrepaidPending = displaySuccessBooking?.payment_pending;
  const showOnHoldBanner = isPrepaidBooking && isPrepaidPending;

  const renderPetIcon = (speciesId: string | number) => {
    const isCat = String(speciesId) === "2" || String(speciesId).toLowerCase() === "cat";
    const dims = { width: 29.518802642822266, height: 22.6875 };
    return isCat ? (
      <Cat {...dims} className="text-[#F05B72] shrink-0" />
    ) : (
      <Dog {...dims} className="text-[#F05B72] shrink-0" />
    );
  };

  const renderSummaryItem = (label: string, value: string) => (
    <div className="flex items-center">
      <span className="text-[#000000] font-medium w-[120px] shrink-0">{label}</span>
      <span className="text-[#000000] font-bold truncate flex-1" title={value}>
        {value}
      </span>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-white select-none overflow-hidden h-screen w-screen">
      <div className="py-4 border-b border-[#E2E8F0] shrink-0 bg-white">
        <BillingMirrorHeader clinicName={clinicName} />
      </div>
      <div className="flex flex-1 overflow-hidden p-6 gap-6 bg-white min-h-0">
        {/* Left Column - Pet Cards */}
        <div className="w-full lg:w-[450px] xl:w-[650px] max-w-[600px] flex flex-col gap-[60px] overflow-y-auto pr-2 shrink xl:shrink-0 scrollbar-hide">
          {activeRecommendations.map((rec) => {
            const isActive = activeTab === rec.pet_id;
            return (
              <div
                key={rec.pet_id}
                onClick={() => setActiveTab(rec.pet_id)}
                className={`flex flex-col transition-all cursor-pointer ${
                  isActive
                    ? "w-full h-[501px] rounded-[20px] border border-[#D5D5D5] gap-[29px] pt-[16px] pr-[24px] pb-[24px] pl-[24px] bg-white shadow-[0px_4px_12px_rgba(240,91,114,0.06)]"
                    : "w-full h-[65px] rounded-[20px] border border-[#D5D5D5] pt-[16px] pr-[24px] pb-[16px] pl-[24px] bg-white opacity-90 justify-center"
                }`}
              >
                {/* Card Header */}
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center gap-3 min-w-0">
                    {renderPetIcon(rec.pet_species_type_id || rec.pet_species_id || rec.pet_species)}
                    <span className="font-medium text-[28px] text-black truncate">{rec.pet_name} Next Booking</span>
                  </div>
                  {isActive && <ChevronDown className="w-5 h-5 text-[#F05B72] transition-transform rotate-180" />}
                </div>
                {/* Expanded Card Body */}
                {isActive && (
                  <div className="flex flex-col gap-4">
                    {/* Reason Section */}
                    <div className="flex flex-col gap-1.5 shrink-0">
                      <div className="w-fit h-[31px] rounded-tl-[20px] rounded-br-[20px] px-[16px] bg-[#FFDAE0] flex items-center justify-center gap-[5px] text-[#F05B72] font-medium text-[18px] whitespace-nowrap leading-none shrink-0">
                        Next Appointment Reason
                      </div>
                      <h4 className="text-[24px] font-semibold text-slate-800">
                        {rec.source === "EMR_FOLLOW_UP" ? "EMR Follow-up" : rec.title || "EMR Follow-up"}
                      </h4>
                       {rec.free_follow_up_strip ? (
                        <div className="text-[20px] font-normal text-slate-500 leading-relaxed">
                          {rec.description ? rec.description.trim() + " " : ""}{rec.free_follow_up_strip.trim()}
                        </div>
                      ) : (
                        <p className="text-[20px] font-normal text-slate-500 leading-relaxed">
                          {rec.description || "Recommendation generated from the doctor's EMR follow-up."}
                        </p>
                      )}
                    </div>
                    {/* Booking Summary Section */}
                    <div className="flex flex-col w-full max-w-[556px] h-[220px] gap-[29px] pt-[10px] shrink-0">
                      <div className="w-fit h-[31px] rounded-tl-[20px] rounded-br-[20px] px-[16px] bg-[#FFDAE0] flex items-center justify-center gap-[5px] text-[#F05B72] font-medium text-[18px] whitespace-nowrap leading-none shrink-0">
                        {rec?.pet_name} Booking Summary
                      </div>
                      <div className="flex flex-col gap-2.5 text-[20px] shrink-0">
                        {renderSummaryItem(
                          "Date",
                          veticDateFormat(slotsFilter?.booking_date || rec?.suggested_booking_date),
                        )}
                        {renderSummaryItem("Service", rec?.service_details?.service_name)}
                        {renderSummaryItem("Doctor", rec?.doctor_name)}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {/* Right Column - Slots Grid or Booking Confirmation details */}
        <div
          ref={slotsScrollRef}
          className="flex-1 w-full min-w-[1144px] h-full bg-[#F9F9F9] rounded-[20px] pt-[32px] pr-[24px] pb-[32px] pl-[24px] gap-[32px] overflow-y-auto flex flex-col min-w-0 border border-[#EBEAEF]"
        >
          {isBooked ? (
            isLoadingBookingDetails ? (
              <div className="flex flex-col items-center justify-center h-full min-h-[300px] space-y-4 py-10 w-full">
                <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
                <p className="text-[18px] font-medium text-slate-500 animate-pulse">Fetching booking details...</p>
              </div>
            ) : displaySuccessBooking ? (
              <div className="space-y-6 w-full py-2">
                {showOnHoldBanner ? (
                  <div className="w-full h-auto rounded-[20px] border border-[#FEE1C0] bg-[#FFFBED] p-[32px] flex flex-col items-center justify-center gap-[24px] text-center">
                    <div className="space-y-3">
                      <p className="text-[26px] font-bold text-[#533200] leading-tight">
                        Booking On Hold - Payment not received yet
                      </p>
                      <p className="text-[18px] font-medium text-black leading-normal max-w-[800px]">
                        This slot is reserved for 15 min. Complete payment to confirm booking or the slot will be
                        released.
                      </p>
                    </div>
                    <AlertTriangle
                      style={{ width: "79.42809295654297px", height: "70.86457824707031px" }}
                      className="text-[#533200]"
                      strokeWidth={1.5}
                    />
                  </div>
                ) : (
                  <div className="w-full h-auto rounded-[20px] border border-[#C0FECA] bg-[#EDFFEE] p-[32px] flex flex-col items-center justify-center gap-[24px] text-center">
                    <div className="space-y-3">
                      <p className="text-[26px] font-bold text-[#005303] leading-tight">Booking Created Successfully</p>
                      <p className="text-[18px] font-medium text-black leading-normal max-w-[800px]">
                        {displaySuccessBooking.payment_collected
                          ? `Appointment has been confirmed. Payment Collected. Confirmation sent to +91 ${displaySuccessBooking.payment_number}.`
                          : "Your appointment has been created successfully. Confirmation sent to registered phone."}
                      </p>
                    </div>
                    <BadgeCheck
                      style={{ width: "79.42809295654297px", height: "70.86457824707031px" }}
                      className="text-[#005303]"
                      strokeWidth={1.5}
                    />
                  </div>
                )}
                <div className="w-full overflow-x-auto pb-2">
                  <div className="w-full min-w-[1100px] space-y-4">
                    {/* Table Header Row (directly on the page background, no box) */}
                    <div className="w-full flex items-center text-left pt-[16px] pb-[16px] pl-[64px] pr-[64px] gap-[55px]">
                      <div className="w-[13%] text-[20px] font-normal text-black tracking-wider">PARENT</div>
                      <div className="w-[15%] text-[20px] font-normal text-black tracking-wider">PET DETAILS</div>
                      <div className="w-[23%] text-[20px] font-normal text-black tracking-wider">SERVICE</div>
                      <div className="w-[14%] text-[20px] font-normal text-black tracking-wider">TIME</div>
                      <div className="w-[17%] text-[20px] font-normal text-black tracking-wider">PROVIDER</div>
                      <div className="w-[18%] text-[20px] font-normal text-black tracking-wider">CLINIC</div>
                    </div>
                    {/* Table Body Row (inside a rounded border card) */}
                    <div className="w-full border border-[#D7D7D7] rounded-[20px] bg-white pt-[16px] pb-[16px] pl-[64px] pr-[64px] flex items-center gap-[55px] shadow-sm">
                      <div className="w-[13%] text-[24px] font-medium text-black flex items-center gap-1.5 min-w-0 pr-2">
                        <span>1.</span>
                        <span className="truncate">{displaySuccessBooking.parent_name}</span>
                      </div>
                      <div className="w-[15%] flex flex-col justify-center min-w-0 pr-2">
                        <span className="text-[24px] font-medium text-black truncate">
                          {displaySuccessBooking.pet_name}
                        </span>
                        <span className="text-[24px] font-normal text-[#727272] truncate mt-0.5">
                          {displaySuccessBooking.pet_species_name} - {displaySuccessBooking.pet_breed_name}
                        </span>
                      </div>
                      <div className="w-[23%] flex flex-col justify-center min-w-0 pr-2">
                        <span className="text-[24px] font-medium text-black truncate">
                          {displaySuccessBooking.service_type_name}
                        </span>
                        <span className="text-[24px] font-normal text-[#727272] truncate mt-0.5">
                          {displaySuccessBooking.sub_service_name}
                        </span>
                      </div>
                      <div className="w-[14%] flex flex-col justify-center min-w-0 pr-2">
                        <span className="text-[24px] font-medium text-black truncate">
                          {addAMPMToTime(displaySuccessBooking.start_time)}
                        </span>
                        <span className="text-[24px] font-normal text-[#727272] truncate mt-0.5">
                          {veticDateFormat(displaySuccessBooking.date)}
                        </span>
                      </div>
                      <div className="w-[17%] text-[24px] font-medium text-black truncate pr-2">
                        {displaySuccessBooking.staff_name}
                      </div>
                      <div className="w-[18%] text-[24px] font-medium text-black truncate">
                        {displaySuccessBooking.clinic_name}
                      </div>
                    </div>
                  </div>
                </div>
                {/* Advance Payment Details box */}
                {displaySuccessBooking.payment_pending && (
                  <div className="w-full h-auto rounded-[12px] border border-[#808DA033] bg-white p-[24px] flex flex-col justify-between shadow-[0_2px_10px_rgba(17,24,39,0.05)]">
                    <div>
                      <h3 className="text-[22px] font-semibold text-black">Advance Payment Details</h3>
                      <div className="mt-6 grid grid-cols-3 gap-8">
                        <div>
                          <p className="text-[16px] font-medium text-[#6D6D6D]">Advance Payment</p>
                          <p className="text-[22px] font-bold text-[#222222] mt-2">
                            ₹{displaySuccessBooking.booking_fee}
                          </p>
                        </div>
                        <div>
                          <p className="text-[16px] font-medium text-[#6D6D6D]">Payment Status</p>
                          <p className="text-[22px] font-bold text-[#FFB814] mt-2">• Pending</p>
                        </div>
                        <div>
                          <p className="text-[16px] font-medium text-[#6D6D6D]">Payment Number</p>
                          <p className="text-[22px] font-bold text-[#222222] mt-2">
                            +91 {displaySuccessBooking.payment_number}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full min-h-[300px] space-y-2 py-10 w-full">
                <p className="text-[18px] font-semibold text-slate-700">Failed to load booking details.</p>
              </div>
            )
          ) : (
            /* Scheduling Grid Screen (Replicates CmSlotsGrid without Filters block) */
            <div className="space-y-6 w-full flex-1 flex flex-col">
              {/* Available Slots Grid - Renders directly on popup background */}
              {displayLoading ? (
                <div className="space-y-6">
                  <div className="flex justify-between items-center">
                    <h4 className="text-[24px] font-bold text-black">Clinic's Available Slots</h4>
                    <span className="text-[24px] font-medium text-[#727272]">{veticDateFormat(displayDate) || ""}</span>
                  </div>
                  {[0, 1].map((row) => (
                    <div key={row} className="space-y-3.5">
                      <div className="h-7 w-48 rounded bg-gray-200 animate-pulse" />
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
                          gap: "19.3px",
                        }}
                        className="pt-1"
                      >
                        {Array.from({ length: 7 }).map((_, idx) => (
                          <div key={idx} className="w-full h-[50px] rounded-[6px] bg-gray-200 animate-pulse" />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : displaySlots.length === 0 ? (
                <div className="space-y-6 flex-1 flex flex-col justify-start">
                  {prepaidBanner}
                  <div className="flex justify-between items-center">
                    <h4 className="text-[24px] font-bold text-black">Clinic's Available Slots</h4>
                    <span className="text-[24px] font-medium text-[#727272]">{veticDateFormat(displayDate) || ""}</span>
                  </div>
                  <div className="flex-1 flex items-center justify-center py-10">
                    <NoDataFoundComponent
                      message="No slots available"
                      imageURL={BASIC}
                      description="No slots available for the selected date / service."
                      className="!w-[300px] md:!w-[300px] xl:!w-[300px] 2xl:!w-[300px]"
                      InputClassName="!my-4"
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-6 flex-1 flex flex-col">
                  {prepaidBanner}
                  <div className="space-y-6">
                    {displaySlots.map((doc) => (
                      <div key={doc.staff_id} className="space-y-3.5">
                        <div className="flex justify-between items-center border-b border-slate-100 pb-2.5">
                          <h4 className="text-[24px] font-bold text-slate-800">{doc.staff_name}</h4>
                          <span className="text-[24px] font-medium text-slate-500">
                            {veticDateFormat(slotsFilter?.booking_date || activeRec?.suggested_booking_date) || ""}
                          </span>
                        </div>
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
                            gap: "19.3px",
                          }}
                          className="pt-1"
                        >
                          {doc.slots.map((slot: any) => {
                            const timeStr = addAMPMToTime(slot.start_time);
                            const isSelected = selectedSlotId === slot.slot_id;
                            const isPrepaid = slot.prepaid_booking_slot?.toLowerCase() === "yes";
                            if (isPrepaid) {
                              return (
                                <button
                                  key={slot.slot_id}
                                  type="button"
                                  onClick={() => setSelectedSlotId(slot.slot_id)}
                                  className={`w-full h-[50px] border rounded-[6px] transition-all focus:outline-none flex items-center justify-center select-none ${
                                    isSelected
                                      ? "border-black font-semibold text-[18px]"
                                      : "border-[#E0E0E0] hover:bg-slate-50 font-medium text-[18px]"
                                  }`}
                                  style={{
                                    background: "linear-gradient(to top, #81B5FF 0%, #193A6A 100%)",
                                  }}
                                >
                                  <div
                                    className={`w-full h-full flex items-center justify-center rounded-tl-2xl rounded-tr-[4px] rounded-b-[4px] text-center ${
                                      isSelected ? "bg-black text-white" : "bg-white text-[#6B7281]"
                                    }`}
                                  >
                                    {timeStr}
                                  </div>
                                </button>
                              );
                            }
                            return (
                              <button
                                key={slot.slot_id}
                                type="button"
                                onClick={() => setSelectedSlotId(slot.slot_id)}
                                className={`w-full h-[50px] border rounded-[6px] transition-all focus:outline-none flex items-center justify-center select-none ${
                                  isSelected
                                    ? "bg-black text-white border-black font-semibold text-[18px]"
                                    : "bg-white text-[#6B7281] border-[#E0E0E0] hover:bg-slate-50 font-medium text-[18px]"
                                }`}
                              >
                                {timeStr}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MirrorFollowUpComponent;
